import { FC, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { format } from 'date-fns';

import { useTranslate } from 'hooks/useTranslate';

import InputCheckbox from 'modules/_shared/components/InputCheckbox';

import {
  EncumbranceExcelGroup,
  EncumbranceExcelRow,
} from '../types/EncumbrancesExcel';
import { getEncumbrancesExcelValidationMessages } from '../utils/getEncumbrancesExcelValidationMessages';
import {
  getEncumbrancesExcelImportSummary,
  isEndDateBeforeToday,
  isHistoricalEligible,
  isImportableGroup,
} from '../utils/encumbrancesExcelImportHelpers';
import { useEncumbrancesExcelModal } from '../context/EncumbrancesExcelModalContext';

type GroupCardProps = {
  group: EncumbranceExcelGroup;
  defaultExpanded: boolean;
  uploadInformationalWithErrors: boolean;
};

const EncumbranceExcelGroupCard: FC<GroupCardProps> = ({
  group,
  defaultExpanded,
  uploadInformationalWithErrors,
}) => {
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const errorMessages = getEncumbrancesExcelValidationMessages(group.errors, t);
  const showEndDateBadge = !group.isValid && Boolean(group.endDate);
  const endDateIsPast = group.endDate
    ? isEndDateBeforeToday(group.endDate)
    : false;
  const importAsHistorical =
    uploadInformationalWithErrors && isHistoricalEligible(group);
  const isImportable = isImportableGroup(group, uploadInformationalWithErrors);

  const renderRow = (row: EncumbranceExcelRow) => (
    <tr key={`${group.encumbranceId}-${row.rowIndex}-${row.role}`}>
      <td>{row.rowIndex}</td>
      <td>{row.role}</td>
      <td>{row.holderCif}</td>
      <td>{row.partyCif || '-'}</td>
      <td>{row.partyName || '-'}</td>
      <td>{row.percentage ?? '-'}</td>
    </tr>
  );

  const getStatusBadge = () => {
    if (importAsHistorical) {
      return (
        <span className="badge badge-info mr-2">
          {t('EncumbrancesExcelHistoricalBadge')}
        </span>
      );
    }

    if (isImportable) {
      return (
        <span className="badge badge-success">
          {t('EncumbrancesExcelEncumbranceValid')}
        </span>
      );
    }

    return (
      <span className="badge badge-danger">
        {t('EncumbrancesExcelEncumbranceHasErrors')}
      </span>
    );
  };

  return (
    <div
      className="encumbrances-excel-modal__group-card"
      data-testid={`encumbrance-group-${group.encumbranceId}`}
    >
      <div
        className="encumbrances-excel-modal__group-header d-flex justify-content-between align-items-center"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="encumbrances-excel-modal__group-header-main d-flex align-items-center">
          <span className="encumbrances-excel-modal__group-id">
            {group.encumbranceId}
          </span>
          <span className="text-soft small">
            {group.type} · {group.shareFrom}-{group.shareTo} · {group.holderCif}
          </span>
        </div>

        <div>
          {showEndDateBadge && group.endDate && endDateIsPast && (
            <span
              className="badge badge-warning mr-2"
              data-testid={`encumbrance-end-date-${group.encumbranceId}`}
            >
              {t('Caducada')}
            </span>
          )}

          {getStatusBadge()}
        </div>
      </div>

      {isExpanded && (
        <div className="encumbrances-excel-modal__group-body">
          {errorMessages.length > 0 && (
            <div className="encumbrances-excel-modal__group-errors mb-3">
              {showEndDateBadge && group.endDate && (
                <span
                  className="badge badge-light float-right"
                  data-testid={`encumbrance-end-date-detail-${group.encumbranceId}`}
                >
                  {t('ExpirationDate')}:{' '}
                  {format(new Date(group.endDate), 'dd/MM/yyyy')}
                </span>
              )}
              <ul className="encumbrances-excel-modal__error-list text-danger small my-2">
                {errorMessages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="encumbrances-excel-modal__table-scroll">
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>{t('EncumbrancesExcelRowColumn')}</th>
                  <th>{t('EncumbrancesExcelRoleColumn')}</th>
                  <th>{t('CIF')}</th>
                  <th>{t('EncumbrancesExcelPartyCif')}</th>
                  <th>{t('Name')}</th>
                  <th>{t('Percentage')}</th>
                </tr>
              </thead>
              <tbody>{group.rows.map(renderRow)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const EncumbrancesExcelPreview: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEncumbrancesExcelModal();
  const preview = data.excelPreview;

  const importSummary = useMemo(() => {
    if (!preview) {
      return null;
    }

    return getEncumbrancesExcelImportSummary({
      groups: preview.groups,
      uploadInformationalWithErrors: data.uploadInformationalWithErrors,
    });
  }, [preview, data.uploadInformationalWithErrors]);

  const errorMessages = useMemo(
    () => getEncumbrancesExcelValidationMessages(preview?.errors || [], t),
    [preview?.errors, t]
  );

  if (!preview) {
    return null;
  }

  if (!preview.groups.length) {
    return (
      <Modal.Body className="encumbrances-excel-modal__preview">
        <p className="text-soft mb-3">{t('EncumbrancesExcelNoEncumbrances')}</p>
      </Modal.Body>
    );
  }

  const hasBlockingGroups = (importSummary?.blockingCount || 0) > 0;
  const hasHistoricalEligible = preview.groups.some((group) =>
    isHistoricalEligible(group)
  );

  return (
    <Modal.Body className="encumbrances-excel-modal__preview">
      {hasBlockingGroups ? (
        <div className="alert alert-warning">
          {t('EncumbrancesExcelPreviewProblems', {
            count: importSummary?.blockingCount || 0,
          })}
        </div>
      ) : (
        <div className="alert alert-success">
          {t('EncumbrancesExcelPreviewReadyToImport', {
            count: importSummary?.importableCount || 0,
          })}
        </div>
      )}

      {hasHistoricalEligible && (
        <div
          className="mb-3"
          data-testid="encumbrances-excel-informational-checkbox"
        >
          <InputCheckbox
            id="encumbrances-excel-upload-informational-with-errors"
            checked={data.uploadInformationalWithErrors}
            label={t('EncumbrancesExcelUploadInformationalWithErrors')}
            onChange={actions.handleToggleUploadInformationalWithErrors}
            disabled={data.isLoading || data.isParsingExcel}
          />
        </div>
      )}

      <div className="row g-gs mb-4">
        {[
          [t('EncumbrancesExcelEncumbrancesCount'), preview.totalEncumbrances],
          [t('EncumbrancesExcelRowsCount'), preview.totalRows],
          [t('EncumbrancesExcelValidCount'), importSummary?.validCount || 0],
          [
            t('EncumbrancesExcelHistoricalCount'),
            importSummary?.historicalCount || 0,
          ],
          [
            t('EncumbrancesExcelBlockingCount'),
            importSummary?.blockingCount || 0,
          ],
        ].map(([label, value]) => (
          <div className="col-sm-6 col-lg-2" key={String(label)}>
            <div className="card card-bordered encumbrances-excel-modal__stat-card">
              <div className="card-inner">
                <span className="text-soft small d-block">{label}</span>
                <span className="encumbrances-excel-modal__stat-value fw-bold">
                  {value}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {errorMessages.length > 0 && (
        <ul
          className="encumbrances-excel-modal__error-list bg-danger-dim text-danger small my-3 py-2 px-3 border-radius"
          data-testid="encumbrances-excel-global-errors"
        >
          {errorMessages.slice(0, 20).map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      {preview.groups.map((group, index) => (
        <EncumbranceExcelGroupCard
          key={group.encumbranceId}
          group={group}
          defaultExpanded={index < 3 || !group.isValid}
          uploadInformationalWithErrors={data.uploadInformationalWithErrors}
        />
      ))}
    </Modal.Body>
  );
};

export default EncumbrancesExcelPreview;
